import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  AlertCircle,
  ArrowLeft,
  Building,
  Calendar,
  CheckCircle2,
  CheckSquare,
  CreditCard,
  Flag,
  Globe,
  Hash,
  Home,
  Image as ImageIcon,
  Layers,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  Tag,
  User,
  X,
} from "lucide-react";
import { FaFacebook, FaInstagram, FaLinkedin, FaTwitter, FaWhatsapp } from "react-icons/fa";
import socioService from "../services/socioService.tsx";
import { TipoSocioNombre } from "../enums/TipoSocio.tsx";
import { getNombreSegmento, Segmento } from "../enums/Segmento.tsx";
import { getNombreRubro, Rubro } from "../enums/Rubro.tsx";
import { useAdminRole } from "../hooks/useAdminRole.ts";
import axios from "axios";

const inputBase =
  "mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none";

type FormState = Record<string, any>;

const initialForm: FormState = {
  socio_nombre: "",
  socio_apellido: "",
  socio_dni: "",
  socio_mail: "",
  socio_nacionalidad: "",
  socio_fechaNacimiento: "",
  socio_cuit: "",
  socio_tipoEmpresa: "",
  socio_domicilio: "",
  socio_localidad_nombre: "",
  socio_celular: "",
  socio_telefono: "",
  socio_otroTelefono: "",
  socio_tipoSocio: "1",
  socio_condicionFiscal: "1",
  socio_segmento: "",
  socio_rubro: [] as number[],
  socio_numero: "",
  socio_acta: "",
  socio_gestion: "",
  socio_Campanias: "",
  socio_estado: "0",
  socio_tieneCajaSeguridad: false,
  socio_enviarMail: true,
  socio_habilitacion: "",
  socio_web: "",
  socio_facebook: "",
  socio_instagram: "",
  socio_twitter: "",
  socio_linkedin: "",
  socio_whatsapp: "",
};

const Section: React.FC<{ title: string; description?: string }> = ({
  title,
  description,
  children,
}) => (
  <div className="space-y-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-5 shadow-sm">
    <div>
      <p className="text-xs uppercase tracking-[0.08em] text-slate-500">{title}</p>
      {description && <p className="text-sm text-slate-500">{description}</p>}
    </div>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
  </div>
);

type FieldProps = {
  name: string;
  label: string;
  icon: React.ElementType;
  type?: string;
  placeholder?: string;
  required?: boolean;
  value?: any;
  min?: number;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
};

const Field: React.FC<FieldProps> = ({
  name,
  label,
  icon: Icon,
  type = "text",
  placeholder,
  required = false,
  value,
  min,
  onChange,
}) => (
  <label className="block rounded-xl text-sm font-medium text-slate-700">
    <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
      <Icon className="h-4 w-4 text-slate-400" />
      {label}
      {required && <span className="text-rose-500">*</span>}
    </span>
    <input
      type={type}
      name={name}
      value={value ?? ""}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      min={min}
      className={inputBase}
    />
  </label>
);

const CheckField: React.FC<{
  name: string;
  label: string;
  icon: React.ElementType;
  checked: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}> = ({ name, label, icon: Icon, checked, onChange }) => (
  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 shadow-sm">
    <input
      type="checkbox"
      name={name}
      checked={checked}
      onChange={onChange}
      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
    />
    <Icon className="h-4 w-4 text-slate-400" />
    {label}
  </label>
);

const toNumberOrNull = (value: any): number | null => {
  if (value === null || value === undefined || String(value).trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export default function CreateSocio() {
  const { getAccessTokenSilently } = useAuth0();
  const { isAdmin } = useAdminRole();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(initialForm);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [servicios, setServicios] = useState<any[]>([]);
  const [serviciosSeleccionados, setServiciosSeleccionados] = useState<number[]>([]);
  const [showServiciosModal, setShowServiciosModal] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  useEffect(() => {
    const fetchServicios = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await axios.get(`${process.env.REACT_APP_BACK_URL}/servicios`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = Array.isArray(res.data) ? res.data : [];
        setServicios(data.filter((s: any) => s.activo !== false));
      } catch (e) {
        // No bloquea el alta
      }
    };
    if (isAdmin) {
      fetchServicios();
    }
  }, [getAccessTokenSilently, isAdmin]);

  if (!isAdmin) return <Navigate to="/inicio" replace />;

  const toggleServicio = (id: number) => {
    setServiciosSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setLogoFile(null);
      setLogoPreview(null);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : undefined;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleRubroToggle = (id: number) => {
    setForm((prev) => {
      const actuales: number[] = prev.socio_rubro || [];
      const nuevos = actuales.includes(id)
        ? actuales.filter((x) => x !== id)
        : [...actuales, id];
      return { ...prev, socio_rubro: nuevos };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const nombre = String(form.socio_nombre || "").trim();
    const apellido = String(form.socio_apellido || "").trim();
    const dni = String(form.socio_dni || "").trim();
    const mail = String(form.socio_mail || "").trim();

    if (!nombre || !apellido || !dni || !mail) {
      setError("Completá nombre, apellido, DNI y email.");
      return;
    }

    const rubros: number[] = form.socio_rubro || [];
    const payload: Record<string, any> = {
      socio_nombre: nombre,
      socio_apellido: apellido,
      socio_dni: dni,
      socio_mail: mail,
      socio_tipoSocio: Number(form.socio_tipoSocio),
      socio_condicionFiscal: Number(form.socio_condicionFiscal),
      socio_nacionalidad: String(form.socio_nacionalidad || "").trim() || undefined,
      socio_fechaNacimiento: form.socio_fechaNacimiento || undefined,
      socio_cuit: String(form.socio_cuit || "").trim() || undefined,
      socio_tipoEmpresa: String(form.socio_tipoEmpresa || "").trim() || undefined,
      socio_domicilio: String(form.socio_domicilio || "").trim() || undefined,
      socio_localidad_nombre:
        String(form.socio_localidad_nombre || "").trim() || undefined,
      socio_celular: String(form.socio_celular || "").trim() || undefined,
      socio_telefono: String(form.socio_telefono || "").trim() || undefined,
      socio_otroTelefono: String(form.socio_otroTelefono || "").trim() || undefined,
      socio_numero: toNumberOrNull(form.socio_numero),
      socio_acta: toNumberOrNull(form.socio_acta),
      socio_gestion: toNumberOrNull(form.socio_gestion),
      socio_Campanias: toNumberOrNull(form.socio_Campanias),
      socio_estado: toNumberOrNull(form.socio_estado),
      socio_segmento: toNumberOrNull(form.socio_segmento),
      socio_rubro: rubros.length ? rubros[0] : null,
      socio_rubros: rubros.length > 1 ? rubros.slice(1).join(",") : null,
      socio_tieneCajaSeguridad: Boolean(form.socio_tieneCajaSeguridad),
      socio_enviarMail: Boolean(form.socio_enviarMail),
      socio_habilitacion: String(form.socio_habilitacion || "").trim() || null,
      socio_web: String(form.socio_web || "").trim() || null,
      socio_facebook: String(form.socio_facebook || "").trim() || null,
      socio_instagram: String(form.socio_instagram || "").trim() || null,
      socio_twitter: String(form.socio_twitter || "").trim() || null,
      socio_linkedin: String(form.socio_linkedin || "").trim() || null,
      socio_whatsapp: String(form.socio_whatsapp || "").trim() || null,
    };

    try {
      setIsSaving(true);
      const creado = await socioService.createSocio(
        payload as any,
        getAccessTokenSilently
      );
      const socioIdCreado = (creado as any).socio_id || (creado as any).id;
      if (logoFile && socioIdCreado) {
        try {
          await socioService.uploadSocioLogo(String(socioIdCreado), logoFile, getAccessTokenSilently);
        } catch (e) {
          // No bloquea el alta si falla el logo
        }
      }
      if (serviciosSeleccionados.length && socioIdCreado) {
        try {
          const token = await getAccessTokenSilently();
          await Promise.all(
            serviciosSeleccionados.map((sid) =>
              axios.post(
                `${process.env.REACT_APP_BACK_URL}/servicios/asociaciones`,
                { socioId: socioIdCreado, servicioId: sid },
                { headers: { Authorization: `Bearer ${token}` } }
              )
            )
          );
        } catch (e) {
          // No bloquea el alta
        }
      }
      setSuccess(
        `Socio ${creado.socio_nombre || nombre} creado correctamente${
          socioIdCreado ? ` (ID ${socioIdCreado})` : ""
        }.`
      );
      setForm(initialForm);
      setServiciosSeleccionados([]);
      setLogoFile(null);
      setLogoPreview(null);
      setTimeout(() => navigate("/admin"), 1200);
    } catch (err: any) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        "No se pudo crear el socio.";
      setError(apiMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-5xl space-y-6 px-4 py-8 sm:px-6 lg:px-10">
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link
                to="/admin"
                className="inline-flex text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                <Home className="mr-2 h-4 w-4" />
                Panel
              </Link>
            </li>
            <li>
              <span className="ml-1 text-sm font-medium text-gray-600 md:ml-2">
                Nuevo socio
              </span>
            </li>
          </ol>
        </nav>

        <div className="rounded-3xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 text-white shadow-xl">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.12em] text-blue-100">
                Socios
              </p>
              <h1 className="text-3xl font-semibold leading-tight">Crear socio</h1>
              <p className="text-sm text-blue-100">
                El email del socio debe coincidir con su cuenta para que pueda ingresar.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 rounded-lg border border-white/30 bg-white/10 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/20"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver
              </button>
              <button
                type="submit"
                form="crear-socio-form"
                disabled={isSaving}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition ${
                  isSaving
                    ? "cursor-not-allowed bg-blue-400"
                    : "bg-blue-700 hover:bg-blue-800"
                }`}
              >
                {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSaving ? "Creando..." : "Crear socio"}
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-xl">
          {success && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" />
              {success}
            </div>
          )}
          {error && (
            <div className="mb-4 flex items-center gap-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <form id="crear-socio-form" className="space-y-4" onSubmit={handleSubmit}>
            <Section
              title="Identidad"
              description="Datos de identificación y contacto principal."
            >
              <Field
                name="socio_nombre"
                label="Nombre"
                icon={User}
                value={form.socio_nombre}
                onChange={handleChange}
                required
              />
              <Field
                name="socio_apellido"
                label="Apellido"
                icon={User}
                value={form.socio_apellido}
                onChange={handleChange}
                required
              />
              <Field
                name="socio_dni"
                label="DNI"
                icon={CreditCard}
                value={form.socio_dni}
                onChange={handleChange}
                required
              />
              <Field
                name="socio_mail"
                label="Domicilio electrónico"
                icon={Mail}
                type="email"
                value={form.socio_mail}
                onChange={handleChange}
                required
                placeholder="cuenta@ejemplo.com"
              />
              <Field
                name="socio_fechaNacimiento"
                label="Fecha de nacimiento"
                icon={Calendar}
                type="date"
                value={form.socio_fechaNacimiento}
                onChange={handleChange}
              />
              <Field
                name="socio_nacionalidad"
                label="Nacionalidad"
                icon={Flag}
                value={form.socio_nacionalidad}
                onChange={handleChange}
              />
            </Section>

            <Section
              title="Ubicación y contacto"
              description="Dirección y teléfonos asociados."
            >
              <Field
                name="socio_domicilio"
                label="Domicilio"
                icon={MapPin}
                value={form.socio_domicilio}
                onChange={handleChange}
              />
              <Field
                name="socio_localidad_nombre"
                label="Localidad"
                icon={MapPin}
                value={form.socio_localidad_nombre}
                onChange={handleChange}
              />
              <Field
                name="socio_celular"
                label="Celular"
                icon={Phone}
                value={form.socio_celular}
                onChange={handleChange}
              />
              <Field
                name="socio_telefono"
                label="Teléfono"
                icon={Phone}
                value={form.socio_telefono}
                onChange={handleChange}
              />
              <Field
                name="socio_otroTelefono"
                label="Otro teléfono"
                icon={Phone}
                value={form.socio_otroTelefono}
                onChange={handleChange}
              />
            </Section>

            <Section
              title="Datos fiscales y empresa"
              description="Información legal y segmentación."
            >
              <Field
                name="socio_cuit"
                label="CUIT"
                icon={Hash}
                value={form.socio_cuit}
                onChange={handleChange}
              />
              <Field
                name="socio_tipoEmpresa"
                label="Tipo de empresa"
                icon={Building}
                value={form.socio_tipoEmpresa}
                onChange={handleChange}
              />
              <label className="block text-sm font-medium text-slate-700">
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <User className="h-4 w-4 text-slate-400" />
                  Tipo de socio <span className="text-rose-500">*</span>
                </span>
                <select
                  name="socio_tipoSocio"
                  value={form.socio_tipoSocio}
                  onChange={handleChange}
                  required
                  className={inputBase}
                >
                  {Object.entries(TipoSocioNombre).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <Field
                name="socio_condicionFiscal"
                label="Condición fiscal"
                icon={Hash}
                type="number"
                min={1}
                value={form.socio_condicionFiscal}
                onChange={handleChange}
                required
                placeholder="1"
              />
              <label className="block text-sm font-medium text-slate-700">
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <Tag className="h-4 w-4 text-slate-400" />
                  Segmento
                </span>
                <select
                  name="socio_segmento"
                  value={form.socio_segmento}
                  onChange={handleChange}
                  className={inputBase}
                >
                  <option value="">Seleccionar segmento...</option>
                  {Object.entries(Segmento).map(([key, val]) => (
                    <option key={key} value={val}>
                      {getNombreSegmento(Number(val))}
                    </option>
                  ))}
                </select>
              </label>
            </Section>

            <Section title="Rubros" description="Podés seleccionar más de uno.">
              <div className="md:col-span-2">
                <div className="flex flex-wrap gap-2">
                  {Object.values(Rubro)
                    .filter((v): v is number => typeof v === "number")
                    .map((val) => {
                      const checked: boolean = (form.socio_rubro || []).includes(val);
                      return (
                        <label
                          key={val}
                          className={`flex cursor-pointer select-none items-center gap-2 rounded-lg border px-3 py-2 text-sm shadow-sm transition ${
                            checked
                              ? "border-blue-500 bg-blue-50 text-blue-700"
                              : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handleRubroToggle(val)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                          />
                          {getNombreRubro(val)}
                        </label>
                      );
                    })}
                </div>
              </div>
            </Section>

            <Section
              title="Datos del negocio"
              description="Logo, habilitación y redes sociales del comercio."
            >
              <div className="md:col-span-2">
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <ImageIcon className="h-4 w-4 text-slate-400" />
                  Logo / Foto
                </span>
                <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="h-24 w-24 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo preview" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-slate-400">
                        <ImageIcon className="h-6 w-6" />
                      </div>
                    )}
                  </div>
                  <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50">
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                    <ImageIcon className="h-4 w-4" />
                    {logoFile ? "Cambiar imagen" : "Seleccionar imagen"}
                  </label>
                  {logoFile && (
                    <button
                      type="button"
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview(null);
                      }}
                      className="flex items-center gap-1 text-sm text-rose-600 hover:text-rose-700"
                    >
                      <X className="h-4 w-4" />
                      Quitar
                    </button>
                  )}
                </div>
              </div>
              <Field
                name="socio_habilitacion"
                label="Habilitación / Matrícula"
                icon={CheckSquare}
                value={form.socio_habilitacion}
                onChange={handleChange}
                placeholder="N° de habilitación, matrícula o permiso"
              />
              <Field
                name="socio_whatsapp"
                label="WhatsApp"
                icon={FaWhatsapp}
                value={form.socio_whatsapp}
                onChange={handleChange}
                placeholder="+54..."
              />
              <Field
                name="socio_web"
                label="Sitio web"
                icon={Globe}
                value={form.socio_web}
                onChange={handleChange}
                placeholder="https://..."
              />
              <Field
                name="socio_facebook"
                label="Facebook"
                icon={FaFacebook}
                value={form.socio_facebook}
                onChange={handleChange}
                placeholder="https://facebook.com/..."
              />
              <Field
                name="socio_instagram"
                label="Instagram"
                icon={FaInstagram}
                value={form.socio_instagram}
                onChange={handleChange}
                placeholder="https://instagram.com/..."
              />
              <Field
                name="socio_twitter"
                label="Twitter / X"
                icon={FaTwitter}
                value={form.socio_twitter}
                onChange={handleChange}
                placeholder="https://x.com/..."
              />
              <Field
                name="socio_linkedin"
                label="LinkedIn"
                icon={FaLinkedin}
                value={form.socio_linkedin}
                onChange={handleChange}
                placeholder="https://linkedin.com/..."
              />
            </Section>

            <Section
              title="Servicios"
              description="Asignale los servicios que presta el socio al momento de crearlo."
            >
              <div className="md:col-span-2">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-slate-600">
                    {serviciosSeleccionados.length
                      ? `${serviciosSeleccionados.length} servicio(s) seleccionado(s)`
                      : "Ningún servicio seleccionado"}
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowServiciosModal(true)}
                    className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    <Plus className="h-4 w-4" />
                    Seleccionar servicios
                  </button>
                </div>
                {serviciosSeleccionados.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {servicios
                      .filter((s) => serviciosSeleccionados.includes(s.servicio_id))
                      .map((s) => (
                        <span
                          key={s.servicio_id}
                          className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700"
                        >
                          {s.nombre}
                          <button
                            type="button"
                            onClick={() => toggleServicio(s.servicio_id)}
                            className="ml-1 text-blue-500 hover:text-blue-700"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                  </div>
                )}
              </div>
            </Section>

            <Section
              title="Administrativo"
              description="Seguimiento interno del socio."
            >
              <Field
                name="socio_numero"
                label="Número de socio"
                icon={Hash}
                type="number"
                value={form.socio_numero}
                onChange={handleChange}
              />
              <Field
                name="socio_acta"
                label="Acta"
                icon={Hash}
                type="number"
                value={form.socio_acta}
                onChange={handleChange}
              />
              <Field
                name="socio_gestion"
                label="Gestión"
                icon={Layers}
                type="number"
                value={form.socio_gestion}
                onChange={handleChange}
              />
              <Field
                name="socio_Campanias"
                label="Campañas"
                icon={Layers}
                type="number"
                value={form.socio_Campanias}
                onChange={handleChange}
              />
              <Field
                name="socio_estado"
                label="Estado"
                icon={CheckSquare}
                type="number"
                value={form.socio_estado}
                onChange={handleChange}
              />
              <div className="space-y-2">
                <CheckField
                  name="socio_tieneCajaSeguridad"
                  label="Tiene caja de seguridad"
                  icon={CheckSquare}
                  checked={!!form.socio_tieneCajaSeguridad}
                  onChange={handleChange}
                />
                <CheckField
                  name="socio_enviarMail"
                  label="Enviar mail"
                  icon={Mail}
                  checked={!!form.socio_enviarMail}
                  onChange={handleChange}
                />
              </div>
            </Section>
          </form>
        </div>
      </div>

      {showServiciosModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4 py-6">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Seleccionar servicios</h2>
                <p className="text-sm text-slate-500">Elegí los servicios que el socio va a prestar</p>
              </div>
              <button
                type="button"
                onClick={() => setShowServiciosModal(false)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
              {servicios.length === 0 ? (
                <p className="text-center text-sm text-slate-500">No hay servicios disponibles</p>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {servicios.map((s) => {
                    const selected = serviciosSeleccionados.includes(s.servicio_id);
                    return (
                      <button
                        type="button"
                        key={s.servicio_id}
                        onClick={() => toggleServicio(s.servicio_id)}
                        className={`flex items-start gap-3 rounded-xl border p-3 text-left transition ${
                          selected
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-200 bg-white hover:border-slate-300"
                        }`}
                      >
                        <div
                          className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded border ${
                            selected ? "border-blue-500 bg-blue-500" : "border-slate-300"
                          }`}
                        >
                          {selected && <CheckCircle2 className="h-3 w-3 text-white" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{s.nombre}</p>
                          {s.descripcion && (
                            <p className="text-xs text-slate-500">{s.descripcion}</p>
                          )}
                          {s.categoria && (
                            <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-500">
                              {s.categoria}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4">
              <p className="text-sm text-slate-500">
                {serviciosSeleccionados.length} seleccionado(s)
              </p>
              <button
                type="button"
                onClick={() => setShowServiciosModal(false)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Listo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
